import type { ReactNode } from "react";

import { ArrowDown2, ArrowUp } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/ngFilters";

import { GrowthOverviewGranularity, type GrowthOverviewMetric } from "../../const";
import {
    GROWTH_OVERVIEW_INCOMPLETE_PERIOD_TOOLTIP_KEYS,
    GROWTH_OVERVIEW_METRIC_LABEL_KEYS,
} from "../../growthOverviewTexts";
import { getGrowthOverviewIntervalEndDate } from "../../utils/growthOverviewInterval";
import { shouldUseLightHeatmapText } from "../../utils/heatmapScale";
import { getPeriodRangeLabel } from "../../utils/periodRange";

const NA_DASH = "—";
const HEATMAP_POP_FONT_SIZE_PX = 14;
const HEATMAP_POP_LINE_HEIGHT_PX = 20;
const HEATMAP_ARROW_SIZE_PX = 10;

const formatPop = (pop: number): string => `${Math.abs(pop).toFixed(1)}%`;

const getHeatmapTextColor = (shouldUseLightText: boolean): string =>
    shouldUseLightText ? colorsPalettes.carbon[0] : colorsPalettes.carbon[500];

interface IHeatmapPercentCellProps {
    pop: number | null;
    absoluteValue: number | null;
    periodDate?: string;
    granularity: GrowthOverviewGranularity;
    metric: GrowthOverviewMetric;
    showHeatmap: boolean;
    isIncomplete?: boolean;
    infoTooltip?: string;
    backgroundColor?: string;
    rowHighlightBackground?: string;
    shouldUseLightText?: boolean;
    dataAutomation?: string;
}

const formatTooltipValue = (value: number): string => numberFilter()(Math.round(value));

const renderHeatmapPop = (pop: number | null, textColor: string) => {
    if (pop == null) {
        return (
            <Text
                as="span"
                fontVariant="body2"
                style={{
                    fontSize: HEATMAP_POP_FONT_SIZE_PX,
                    lineHeight: `${HEATMAP_POP_LINE_HEIGHT_PX}px`,
                    color: textColor,
                    whiteSpace: "nowrap",
                }}
            >
                {NA_DASH}
            </Text>
        );
    }

    if (pop === 0) {
        return (
            <Text
                as="span"
                fontVariant="body2"
                style={{
                    fontSize: HEATMAP_POP_FONT_SIZE_PX,
                    lineHeight: `${HEATMAP_POP_LINE_HEIGHT_PX}px`,
                    color: textColor,
                    whiteSpace: "nowrap",
                }}
            >
                0%
            </Text>
        );
    }

    return (
        <Flex
            as="span"
            alignItems="center"
            gap="2px"
            style={{ color: textColor, whiteSpace: "nowrap" }}
        >
            {pop > 0 ? (
                <ArrowUp
                    size={HEATMAP_ARROW_SIZE_PX}
                    color={textColor}
                    style={{ flexShrink: 0 }}
                />
            ) : (
                <ArrowDown2
                    size={HEATMAP_ARROW_SIZE_PX}
                    color={textColor}
                    style={{ flexShrink: 0 }}
                />
            )}
            <Text
                as="span"
                fontVariant="body2"
                style={{
                    fontSize: HEATMAP_POP_FONT_SIZE_PX,
                    lineHeight: `${HEATMAP_POP_LINE_HEIGHT_PX}px`,
                    color: textColor,
                }}
            >
                {formatPop(pop)}
            </Text>
        </Flex>
    );
};

export const HeatmapPercentCell = ({
    pop,
    absoluteValue,
    periodDate,
    granularity,
    metric,
    showHeatmap,
    isIncomplete = false,
    infoTooltip,
    backgroundColor,
    rowHighlightBackground,
    shouldUseLightText = false,
    dataAutomation,
}: IHeatmapPercentCellProps) => {
    const hasAbsoluteValue = absoluteValue != null;
    const hasValueTooltip = hasAbsoluteValue && periodDate != null && !isIncomplete;
    const cellBackgroundColor = showHeatmap ? backgroundColor : rowHighlightBackground;
    const lightTextColor = shouldUseLightText || shouldUseLightHeatmapText(cellBackgroundColor);
    const textColor = getHeatmapTextColor(lightTextColor);

    const cell = (
        <Flex
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="100%"
            style={{
                minHeight: 48,
                backgroundColor: cellBackgroundColor ?? "transparent",
            }}
            data-automation={dataAutomation}
        >
            {renderHeatmapPop(pop, textColor)}
        </Flex>
    );

    const wrapWithTooltip = (content: ReactNode): ReactNode => (
        <Tooltip
            placement="top"
            content={content}
        >
            <Box
                display="flex"
                flex="1"
                alignSelf="stretch"
                width="100%"
                style={{
                    minWidth: 0,
                    backgroundColor: cellBackgroundColor ?? "transparent",
                }}
            >
                {cell}
            </Box>
        </Tooltip>
    );

    if (infoTooltip) {
        return wrapWithTooltip(infoTooltip);
    }

    if (!hasValueTooltip && !isIncomplete) {
        return cell;
    }

    if (isIncomplete) {
        return wrapWithTooltip(
            translate(
                GROWTH_OVERVIEW_INCOMPLETE_PERIOD_TOOLTIP_KEYS[
                    granularity === GrowthOverviewGranularity.Weekly
                        ? GrowthOverviewGranularity.Weekly
                        : GrowthOverviewGranularity.Quarterly
                ],
            ),
        );
    }

    if (periodDate == null) {
        return cell;
    }

    const intervalEnd = getGrowthOverviewIntervalEndDate();
    const periodRangeLabel = getPeriodRangeLabel(periodDate, granularity, intervalEnd);
    const exactValueLabel = hasAbsoluteValue ? formatTooltipValue(absoluteValue) : null;

    return wrapWithTooltip(
        <Flex
            flexDirection="column"
            gap="2px"
        >
            <Text fontVariant="subtitle2">{translate(GROWTH_OVERVIEW_METRIC_LABEL_KEYS[metric])}</Text>
            <Text fontVariant="subtitle2">{periodRangeLabel}</Text>
            {exactValueLabel ? <Text fontVariant="body2">{exactValueLabel}</Text> : null}
        </Flex>,
    );
};
